/**
 * Jul-2026 (User-Wunsch) — E-Mail-Center & E-Mail-Freigabe als Tabs im
 * Dashboard-Widget „Aufgaben & Hinweise": kompakte Übersicht mit Badge,
 * Direkt-Aktionen (Freigeben/Verwerfen) und Sprung in die Vollansicht.
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Mail, MailCheck, Loader2, ExternalLink, Check, X, Inbox, Paperclip, ChevronDown, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const fmtDt = (iso) => { try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export function EmailCenterPanel() {
    const [accounts, setAccounts] = useState(null);
    // Jul-2026 (User-Wunsch) — Posteingang direkt im Tab anzeigen (nicht nur Konten).
    const [expanded, setExpanded] = useState(null); // account_id
    const [msgs, setMsgs] = useState({}); // account_id -> items|null(lädt)

    const loadMsgs = useCallback((accId) => {
        setMsgs((m) => ({ ...m, [accId]: m[accId] || null }));
        api.get(`/email/messages?account_id=${accId}&folder=inbox&view=open&q=`)
            .then((r) => setMsgs((m) => ({ ...m, [accId]: (r.data?.items || []).slice(0, 8) })))
            .catch(() => setMsgs((m) => ({ ...m, [accId]: [] })));
    }, []);

    useEffect(() => {
        let active = true;
        api.get("/email/accounts")
            .then((r) => {
                if (!active) return;
                const items = r.data?.items || [];
                setAccounts(items);
                if (items.length > 0) {
                    const first = items.find((a) => (a.unread || 0) > 0) || items[0];
                    setExpanded(first.id);
                    loadMsgs(first.id);
                }
            })
            .catch(() => { if (active) setAccounts([]); });
        return () => { active = false; };
    }, [loadMsgs]);

    const toggle = (a) => {
        if (expanded === a.id) { setExpanded(null); return; }
        setExpanded(a.id);
        if (!msgs[a.id]) loadMsgs(a.id);
    };

    if (accounts === null) {
        return <div className="bg-white border border-gray-200 rounded-sm p-6 flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Lade Postfächer…</div>;
    }
    const totalUnread = accounts.reduce((s, a) => s + (a.unread || 0), 0);
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="email-center-panel">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-black text-base flex items-center gap-2"><Mail className="w-4 h-4 text-teal-700" /> E-Mail-Center</h3>
                <Link to="/dealer/email" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-teal-700 hover:text-teal-900" data-testid="email-center-open-link">
                    Öffnen <ExternalLink className="w-3.5 h-3.5" />
                </Link>
            </div>
            {accounts.length === 0 ? (
                <div className="text-center py-6" data-testid="email-center-empty">
                    <Mail className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <div className="font-display font-black text-base text-[#111827]">Noch kein Postfach verbunden</div>
                    <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
                        Verbinden Sie Ihr bestehendes Postfach (IMAP — z. B. IONOS, Strato, Outlook 365) oder legen Sie eine System-Adresse an. Danach: Posteingang, Antworten mit KI, Vorlagen &amp; Wiedervorlagen — alles hier im System.
                    </p>
                    <Link
                        to="/dealer/email?connect=1"
                        className="inline-flex items-center gap-1.5 mt-4 bg-[#111827] hover:bg-black text-white rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider"
                        data-testid="email-center-connect-btn"
                    >
                        <Plus className="w-4 h-4" /> Postfach verbinden
                    </Link>
                </div>
            ) : (
                <div className="divide-y divide-gray-100" data-testid="email-center-accounts">
                    {accounts.map((a) => (
                        <div key={a.id}>
                            <button type="button" onClick={() => toggle(a)}
                                className="w-full flex items-center gap-2 py-2 text-sm text-left hover:bg-gray-50 rounded-sm px-1 -mx-1"
                                title="Klicken zum Auf-/Zuklappen des Posteingangs"
                                data-testid={`email-center-account-${a.id}`}>
                                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${expanded === a.id ? "" : "-rotate-90"}`} />
                                <Inbox className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                <span className="flex-1 truncate font-bold">{a.email || a.label || a.id}</span>
                                {a.unread > 0 ? (
                                    <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-teal-700 text-white text-[11px] font-mono-num font-bold" data-testid={`email-center-unread-${a.id}`}>{a.unread}</span>
                                ) : (
                                    <span className="text-[11px] text-gray-400">0 ungelesen</span>
                                )}
                            </button>
                            {expanded === a.id && (
                                <div className="pb-2 pl-6" data-testid={`email-center-inbox-${a.id}`}>
                                    {msgs[a.id] === null || msgs[a.id] === undefined ? (
                                        <div className="text-xs text-gray-400 flex items-center gap-1.5 py-1"><Loader2 className="w-3 h-3 animate-spin" /> Lade Posteingang…</div>
                                    ) : msgs[a.id].length === 0 ? (
                                        <div className="text-xs text-gray-400 py-1">Keine offenen E-Mails im Posteingang.</div>
                                    ) : (
                                        <ul className="divide-y divide-gray-50">
                                            {msgs[a.id].map((m) => (
                                                <li key={m.id}>
                                                    <Link to={`/dealer/email?open=${m.id}`}
                                                        className="flex items-center gap-2 py-1.5 hover:bg-gray-50 rounded-sm px-1 -mx-1"
                                                        data-testid={`email-center-msg-${m.id}`}>
                                                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${m.unread ? "bg-teal-600" : "bg-gray-200"}`} />
                                                        <span className={`text-xs truncate w-36 shrink-0 ${m.unread ? "font-bold text-gray-900" : "text-gray-600"}`}>{m.from_name || m.from_addr || "?"}</span>
                                                        <span className={`text-xs truncate flex-1 ${m.unread ? "font-bold text-gray-900" : "text-gray-500"}`}>{m.subject || "(kein Betreff)"}</span>
                                                        <span className="text-[10px] text-gray-400 shrink-0 font-mono-num">{fmtDt(m.date)}</span>
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {totalUnread > 0 && (
                <div className="mt-2 text-xs text-gray-500">{totalUnread} ungelesene E-Mail{totalUnread === 1 ? "" : "s"} insgesamt.</div>
            )}
        </div>
    );
}

export function EmailApprovalPanel({ onCountChange }) {
    const [items, setItems] = useState(null);
    const [forbidden, setForbidden] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [preview, setPreview] = useState(null); // Volltext der angeklickten Mail
    const [previewLoading, setPreviewLoading] = useState(false);

    const openPreview = async (m) => {
        setPreviewLoading(true);
        try {
            const r = await api.get(`/dealer/email-outbox/${m.id}`);
            setPreview(r.data);
        } catch (e) { toast.error(e.response?.data?.detail || "Vorschau konnte nicht geladen werden"); }
        finally { setPreviewLoading(false); }
    };

    const load = useCallback(() => {
        api.get("/dealer/email-outbox", { params: { status: "pending", limit: 8 } })
            .then((r) => {
                setItems(r.data?.items || []);
                onCountChange?.(r.data?.counts?.pending ?? (r.data?.items || []).length);
            })
            .catch((e) => {
                if (e.response?.status === 403) setForbidden(true);
                setItems([]);
            });
    }, [onCountChange]);
    useEffect(() => { load(); }, [load]);

    const act = async (oid, action) => {
        setBusyId(oid);
        try {
            await api.post(`/dealer/email-outbox/${oid}/${action}`);
            toast.success(action === "approve" ? "E-Mail freigegeben & versendet" : "E-Mail verworfen");
            setPreview(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); }
        setBusyId(null);
    };

    if (items === null) {
        return <div className="bg-white border border-gray-200 rounded-sm p-6 flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Lade Postausgang…</div>;
    }
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="email-approval-panel">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-black text-base flex items-center gap-2"><MailCheck className="w-4 h-4 text-indigo-700" /> E-Mail-Freigabe</h3>
                <Link to="/dealer/email-freigabe" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-indigo-700 hover:text-indigo-900" data-testid="email-approval-open-link">
                    Alle ansehen <ExternalLink className="w-3.5 h-3.5" />
                </Link>
            </div>
            {forbidden ? (
                <p className="text-sm text-gray-500" data-testid="email-approval-forbidden">Die Freigabe ist dem Konto-Inhaber bzw. Filialleitern vorbehalten.</p>
            ) : items.length === 0 ? (
                <p className="text-sm text-gray-500" data-testid="email-approval-empty">Keine E-Mails warten auf Freigabe — alles versendet. ✔</p>
            ) : (
                <ul className="divide-y divide-gray-100" data-testid="email-approval-list">
                    {items.map((m) => (
                        <li key={m.id} className="py-2 flex items-center gap-2" data-testid={`email-approval-item-${m.id}`}>
                            <button type="button" onClick={() => openPreview(m)}
                                className="flex-1 min-w-0 text-left hover:bg-gray-50 rounded-sm px-1 -mx-1 py-0.5"
                                title="Zum Ansehen klicken"
                                data-testid={`email-approval-preview-${m.id}`}>
                                <div className="text-sm font-bold truncate text-indigo-800 hover:underline">{m.subject || "(ohne Betreff)"}</div>
                                <div className="text-[11px] text-gray-500 truncate">an {m.to} · {fmtDt(m.created_at)}</div>
                                {m.vehicle_ctx && (
                                    <div className="text-[11px] text-gray-600 truncate" data-testid={`email-approval-vehicle-${m.id}`}>
                                        {m.vehicle_ctx.vehicle_number && <span className="font-mono-num font-bold">{m.vehicle_ctx.vehicle_number}</span>}
                                        {m.vehicle_ctx.seller_name && <span>{m.vehicle_ctx.vehicle_number ? " · " : ""}Verkäufer: {m.vehicle_ctx.seller_name}</span>}
                                        {m.vehicle_ctx.sale_date && <span> · verkauft am {new Date(m.vehicle_ctx.sale_date).toLocaleDateString("de-DE")}</span>}
                                    </div>
                                )}
                            </button>
                            <button type="button" disabled={busyId === m.id} onClick={() => act(m.id, "approve")}
                                className="inline-flex items-center gap-1 h-8 px-2.5 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-50"
                                data-testid={`email-approval-approve-${m.id}`}>
                                {busyId === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Freigeben
                            </button>
                            <button type="button" disabled={busyId === m.id} onClick={() => act(m.id, "discard")}
                                className="inline-flex items-center gap-1 h-8 px-2 rounded-sm border border-red-300 text-red-700 hover:bg-red-50 text-[11px] font-bold uppercase tracking-wider disabled:opacity-50"
                                data-testid={`email-approval-discard-${m.id}`}>
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {previewLoading && (
                <div className="mt-2 text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Lade Vorschau…</div>
            )}
            {preview && (
                <Dialog open onOpenChange={(o) => !o && setPreview(null)}>
                    <DialogContent className="max-w-3xl overflow-hidden" data-testid="email-approval-preview-dialog">
                        <DialogHeader className="min-w-0"><DialogTitle className="pr-6 truncate block">{preview.subject || "(ohne Betreff)"}</DialogTitle></DialogHeader>
                        <div className="text-xs text-gray-600 space-y-0.5">
                            <div><b>An:</b> {preview.to}{(preview.cc || []).length > 0 && <> · <b>CC:</b> {(preview.cc || []).join(", ")}</>}</div>
                            <div><b>Erstellt:</b> {fmtDt(preview.created_at)}{preview.triggered_by_name && <> · <b>Ausgelöst von:</b> {preview.triggered_by_name}</>}</div>
                            {(preview.attachments || []).length > 0 && (
                                <div className="flex items-center gap-1 flex-wrap">
                                    <Paperclip className="w-3 h-3" />
                                    {(preview.attachments || []).map((a, i) => (
                                        <span key={i} className="font-bold">{a.filename}{a.size_kb ? ` (${a.size_kb} KB)` : ""}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                        {preview.body_html
                            ? <iframe title="E-Mail-Vorschau" srcDoc={preview.body_html} sandbox="" className="w-full h-[45vh] bg-white border rounded" data-testid="email-approval-preview-iframe" />
                            : <pre className="whitespace-pre-wrap font-sans text-sm max-h-[45vh] overflow-y-auto border rounded p-3 bg-gray-50">{preview.body_text || "(kein Inhalt)"}</pre>}
                        <div className="flex items-center justify-end gap-2">
                            <button type="button" disabled={busyId === preview.id} onClick={() => act(preview.id, "discard")}
                                className="inline-flex items-center gap-1 h-9 px-3 rounded-sm border border-red-300 text-red-700 hover:bg-red-50 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                                data-testid="email-approval-dialog-discard">
                                <X className="w-4 h-4" /> Verwerfen
                            </button>
                            <button type="button" disabled={busyId === preview.id} onClick={() => act(preview.id, "approve")}
                                className="inline-flex items-center gap-1 h-9 px-3 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                                data-testid="email-approval-dialog-approve">
                                {busyId === preview.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Freigeben & senden
                            </button>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
